import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  CHECKBOX_LAYOUT_MODE_OPTIONS_WITH_IMAGES,
  type ElementSizingMode,
  getActiveBinding,
  isBindingActive,
  type OptionPickerDefaultValue,
  OPTIONS_PICKER_DEFAULT_VALUE_OPTIONS,
  type PropertiesConfig,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, SCHEMA_COLUMNS_SELECT_OPTION_ARRAY } from '@blaze-shared/schema-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';
import { TILE_SELECTOR_IMAGE_RATIOS } from '../radio/tile-selector-element';

import { CheckboxDefinitionMapper } from './ai/checkbox.definition-mapper';
import { CheckboxElementHandler } from './ai/checkbox.element-handler';
import { CheckboxComponent } from './checkbox.component';
import { type CheckboxPropsModel } from './checkbox.model';
import { CheckboxPropsBuilder } from './checkbox.props-builder';
import { generateCheckboxFormField } from './generation/form-field';
import { checkboxV2Migration } from './migrations/002-checkbox.migration';
import { checkboxV3Migration } from './migrations/003-checkbox.migration';
import { checkboxV4PaddingToMarginMigration } from './migrations/004-checkbox-padding-to-margin.migration';

export { transformToCheckbox } from './checkbox.transforms';
export type CheckboxElement = CanvasElementFrom<'checkbox'>;
export type CheckboxDef = WithProps<CheckboxPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    checkbox: CheckboxDef;
  }
}

const DEFAULT_WIDTH = 356;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'checkbox',
  componentType: CheckboxComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Checkbox group',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: CheckboxPropsBuilder,
  generation: {
    formField: generateCheckboxFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.image',
    'content.icon',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      readonly: 'content.checkbox.readonly',
    },
  },
  features: {
    ai: {
      optionalInput: true,
    },
    optionsSortSupport: true,
    searchAndFiltersFieldSupport: true,
  },
  ai: {
    name: 'checkboxGroup',
    handler: () => CheckboxElementHandler,
    definitionMapper: () => CheckboxDefinitionMapper,
  },
  version: 4,
  migrations: {
    2: checkboxV2Migration,
    3: checkboxV3Migration,
    4: checkboxV4PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'optionsList',
        isDefaultDataSourceInput: true,
        bindingDefs: [
          { property: 'value', label: 'Option value field', dataType: 'String', value: true },
          { property: 'label', label: 'Option label field', dataType: 'String', display: true },
        ],
      },
      {
        name: 'selected',
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'selected', label: 'Selected value', dataType: 'Array' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        // Default schema before binding consists of label, value pair. If bound, this will change.
        schema: {
          id: '',
          name: 'Checkbox',
          columns: SCHEMA_COLUMNS_SELECT_OPTION_ARRAY,
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'checkbox'> {
  return {
    content: {
      title: {
        label: 'Checkbox',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      data: {
        defaultValue: 'none',
      },
      checkbox: {
        values: [],
        preselect: ['Option 1'],
        layout: 'row',
        maxColumns: { mode: 'value', value: 1 },
        minWidth: { mode: 'value', value: 320 },
        cardAlign: 'left',
      },
      customFields: [],
      sort: {
        field: '',
        direction: '',
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      fontWeight: '$t.labelFontWeight',
      fontStyle: [],
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontSize: '$t.fontSize',
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight',
        lineHeight: '$t.lineHeight',
        fontStyle: [],
        iconSize: '16px',
      },
      checkbox: {
        label: { color1: '$t.value', selectorType: 'solid' },
        symbol: { color1: '$t.accentLabel', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
        padding: '16px',
        gap: '8px',
        tileRadius: '4px',
        checkmarkSize: '24px',
      },
      box: {
        subText: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontSize: '13px',
          lineHeight: '$t.lineHeight',
        },
      },
      card: {
        heading: {
          align: 'left',
        },
        subheading: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontSize: '16px',
          fontWeight: '$t.labelFontWeight',
          fontStyle: [],
          spaceAbove: '5px',
          align: 'left',
        },
        description: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontSize: '13px',
          fontWeight: '$t.labelFontWeight',
          spaceAbove: '16px',
          align: 'left',
        },
        customField: {
          color: { color1: '$t.value', selectorType: 'solid' },
          fontSize: '13px',
          spaceAbove: '16px',
          align: 'left',
        },
      },
    },
  };
}

function isImageFieldMissing(data: CanvasElementDataProperties[] | undefined): boolean {
  const binding = getActiveBinding(data, 'optionsList');
  return !!binding && !binding.bindings?.['imageSrc'];
}

function hasCardBindingField(
  data: CanvasElementDataProperties[] | undefined,
  property: string,
): boolean {
  return !!getActiveBinding(data, 'optionsList')?.bindings?.[property];
}

function getPropsConfig(): PropertiesConfig {
  const selectMode = [
    epc.utils.observedField(
      epc.fields.selectFieldFromImageList(
        'checkbox-template',
        'content.mode',
        'Template',
        CHECKBOX_LAYOUT_MODE_OPTIONS_WITH_IMAGES,
        { allowNone: false, showSelection: true, hideLabel: true },
      ),
      (_, value) => {
        if (value === 'card') {
          return {
            //heading
            'style.fontSize': '20px',
            'style.fontWeight': '600',
            //subheading
            'style.card.subheading.fontSize': '16px',
            'style.card.subheading.fontWeight': '400',
            //description
            'style.card.description.fontSize': '13px',
            'style.card.description.fontWeight': '400',

            'style.checkbox.padding': '16px',
            'style.checkbox.gap': '8px',
            'style.checkbox.tileRadius': '4px',
            'style.checkbox.checkmarkSize': '16px',

            'content.showCheckIcon': true,

            'content.image.ratio': 'circle',
            'content.image.position': 'right',
            'content.image.width': '32px',
          };
        } else if (value === 'image') {
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.checkbox.padding': '16px',
            'style.checkbox.gap': '8px',
            'style.checkbox.tileRadius': '4px',
            'style.checkbox.checkmarkSize': '24px',

            'content.image.ratio': '1:1',
            'content.image.position': 'top',
            'content.image.width': '80px',
          };
        } else if (value === 'box') {
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.fontStyle': [],
            'style.checkbox.padding': '16px',
            'style.checkbox.gap': '8px',
            'style.checkbox.tileRadius': '4px',
            'style.checkbox.checkmarkSize': '24px',

            'content.icon.position': 'left',
            'content.icon.size': '24px',
          };
        } else if (value === 'chip') {
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.fontStyle': [],
            'style.checkbox.padding': '8px',
            'style.checkbox.gap': '4px',
            'style.checkbox.tileRadius': '25px',
            'style.checkbox.checkmarkSize': '24px',
          };
        } else {
          // value === 'text'
          return {
            'style.fontSize': '$t.fontSize',
            'style.fontWeight': '$t.fontWeight',
            'style.fontStyle': [],
            'style.checkbox.padding': '16px',
            'style.checkbox.gap': '8px',
            'style.checkbox.tileRadius': '4px',
            'style.checkbox.checkmarkSize': '24px',
          };
        }
      },
    ),
  ];

  return epc.initConfig('checkbox', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:optionsList.enabled',
        'Data',
        [
          epc.utils.conditionalField(
            epc.fields.tileWithCheckboxItemValues('content.checkbox', 'Values', {
              withSubText: true,
            }),
            'content.mode',
            (mode, data) => mode === 'card' && !getActiveBinding(data, 'optionsList'),
          ),
          epc.utils.conditionalField(
            epc.utils.separator('', { labelOnLine: 'Set dynamic source' }),
            'content.mode',
            (mode) => mode !== 'card',
          ),
        ],
        [
          epc.fields.selectDataSourceField({
            key: 'optionsList',
            label: 'Values',
            sources: 'list',
            bindings: [
              { property: 'value', label: 'Option value field', dataType: 'String', value: true },
              {
                property: 'imageSrc',
                label: 'Option image field',
                dataType: 'String',
                value: true,
              },
              {
                property: 'icon',
                label: 'Option icon field',
                dataType: 'String',
                value: true,
              },
              { property: 'label', label: 'Option value field', dataType: 'String', display: true },
            ],
          }),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromSchema(
                'data.?key:optionsList.bindings.label',
                'Display',
                'data.?key:optionsList.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.imageSrc',
                  'Image',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'image' && mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.subheading',
                  'Subheading',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.subheading',
                  'Sub text',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'box',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.description',
                  'Description',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'data.?key:optionsList.bindings.icon',
                  'Icon',
                  'data.?key:optionsList.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                    allowNone: true,
                  },
                ),
                'content.mode',
                (mode) => mode !== 'box',
              ),
              epc.fields.sortField(
                'content.sort',
                'Sort by',
                'data.?key:optionsList.dataSource.columns',
                'name',
                COLUMN_ID_OR_NAME,
              ),
              epc.utils.conditionalField(
                epc.utils.separator(),
                'content.mode',
                (mode) => mode !== 'card',
              ),
              epc.utils.conditionalField(
                epc.fields.listCustomFieldsField('content.customFields', 'Add Custom Field', {
                  source: 'schema',
                  datasourcePath: 'data.?key:optionsList.dataSource',
                  useMetaValue: false,
                }),
                'content.mode',
                (mode) => mode !== 'card',
              ),
            ],
            'data.?key:optionsList',
            (value) => !isBindingActive(value),
          ),
        ],
      ),

      epc.subsections.subsection('content', 'Template', selectMode),

      epc.subsections.subsection(
        'content.checkbox',
        'Options',
        [
          epc.fields.selectFieldFromList(
            'content.data.defaultValue',
            'Default value',
            OPTIONS_PICKER_DEFAULT_VALUE_OPTIONS,
            {
              showSelection: true,
              allowNone: false,
              optionsFilter: (model: PropsOf<'dropdown'>, options) =>
                !getValueByPath(model, `data.?key:optionsList.enabled`)
                  ? (options as SelectOption<OptionPickerDefaultValue>[])?.filter(
                      (o) => o.value !== 'placeholder',
                    )
                  : (options as SelectOption<OptionPickerDefaultValue>[])?.filter(
                      (o) => o.value !== 'placeholder' && o.value !== 'fixedValue',
                    ),
            },
          ),

          epc.utils.conditionalField(
            // pick from list of `values`
            epc.fields.selectFieldFromModel(
              'content.checkbox.preselect',
              'Value',
              'content.checkbox.values',
              { allowNone: false, showSelection: true, allowMultiSelect: true },
            ),
            'content.data.defaultValue',
            (modelValue) => (modelValue as OptionPickerDefaultValue) !== 'fixedValue',
          ),

          epc.fields.layoutField('content.checkbox.layout'),
          ...epc.utils.conditionalFields(
            [
              epc.fields.sizeField({
                path: 'content.checkbox.maxColumns',
                label: 'Max columns',
                units: [{ min: 1, max: 10 }],
                sizingOptions: [{ label: 'Min', value: 'min' }],
                showArrows: true,
              }),
              epc.fields.sizeField({
                path: 'content.checkbox.minWidth',
                label: 'Min width',
                units: [{ max: 400, min: 100, unit: 'px' }],
                sizingOptions: [{ label: 'Min', value: 'min' }],
                showArrows: true,
              }),
            ],
            'content.checkbox.layout',
            (target) => target !== 'column',
          ),
          epc.fields.positionField(
            'content.image.position',
            'Image position',
            [
              { value: 'right', label: '', icon: 'icon_aligns_to_left_16' },
              { value: 'left', label: '', icon: 'icon_aligns_to_right_16' },
            ],
            { defaultValue: 'right' },
            {
              hideWhen: (model) => model.content.mode !== 'card',
            },
          ),
          epc.fields.toggleField('content.showCheckIcon', 'Show checkmark icon', {
            hideWhen: (model) => ['text', 'image', 'chip'].includes(model.content.mode),
          }),
          epc.utils.conditionalField(
            epc.fields.horizontalAlignField(`content.checkbox.cardAlign`, {
              defaultValue: undefined,
              label: 'Card Alignment',
            }),
            'content',
            (modelValue: CheckboxPropsModel['content']) =>
              !(
                ['image', 'box'].includes(modelValue.mode ?? '') &&
                modelValue.checkbox.layout === 'column'
              ),
          ),

          epc.fields.toggleField('content.checkbox.readonly', 'Read Only'),

          epc.utils.conditionalField(
            epc.utils.separator(),
            'content.mode',
            (value, model) =>
              value === 'text' ||
              value === 'chip' ||
              (value === 'card' && !isBindingActive(model.data)) ||
              ((value === 'image' || value === 'card') && isImageFieldMissing(model.data)),
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromList(
                'content.image.ratio',
                'Ratio',
                TILE_SELECTOR_IMAGE_RATIOS,
                {
                  allowNone: false,
                  showSelection: true,
                },
              ),
              epc.utils.conditionalField(
                epc.fields.positionField(
                  'content.image.position',
                  'Position',
                  [
                    { value: 'top', label: 'Top', icon: 'icon_aligns_to_top_16' },
                    { value: 'bottom', label: 'Bottom', icon: 'icon_aligns_to_bottom_16' },
                    { value: 'left', label: 'Left', icon: 'icon_aligns_to_left_16' },
                    { value: 'right', label: 'Right', icon: 'icon_aligns_to_right_16' },
                  ],
                  { defaultValue: 'left' },
                  { renderType: 'icon' },
                ),
                'content.mode',
                (mode) => mode !== 'image',
              ),
              epc.fields.sliderField('content.image.width', 'Width', {
                units: [{ min: 20, max: 800, unit: 'px' }],
              }),
              epc.fields.sliderField('content.image.height', 'Height', {
                units: [{ min: 20, max: 800, unit: 'px' }],
                disableWhen: (model) => {
                  if (getValueByPath(model, 'content.image.ratio') !== 'none') {
                    return 'available only for unrestricted ratio';
                  }
                  return undefined;
                },
              }),
              epc.fields.imageBehaviorField('content.image.behavior'),
            ],
            'content.mode',
            (mode, model) =>
              (mode !== 'image' && mode !== 'card') || isImageFieldMissing(model.data),
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.positionField(
                'content.icon.position',
                'Position',
                [
                  { value: 'top', label: 'Top', icon: 'icon_aligns_to_top_16' },
                  { value: 'bottom', label: 'Bottom', icon: 'icon_aligns_to_bottom_16' },
                  { value: 'left', label: 'Left', icon: 'icon_aligns_to_left_16' },
                  { value: 'right', label: 'Right', icon: 'icon_aligns_to_right_16' },
                ],
                { defaultValue: 'left' },
                { renderType: 'icon' },
              ),
              epc.fields.sliderField('content.icon.size', 'Size', {
                units: [{ min: 8, max: 100, unit: 'px' }],
              }),
            ],
            'content.mode',
            (mode) => mode !== 'box',
          ),
        ],

        {
          dataBindings: [
            {
              key: 'selected',
              label: 'Selected Value',
              sources: 'field',
              bindings: [{ property: 'selected', label: 'Selected value', dataType: 'Array' }],
            },
          ],
        },
      ),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection(
          'style.checkbox',
          'Checkbox',
          [
            epc.fields.colorField('style.checkbox.symbol', 'Symbol', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.colorField('style.checkbox.fill', 'Fill', {
              allowGradient: false,
              allowNone: false,
            }),
            ...epc.utils.conditionalFields(
              [
                epc.fields.radiusField('style.checkbox.radius'),
                epc.fields.colorField('style.checkbox.border', 'Border', {
                  allowGradient: false,
                }),
              ],
              'content.checkbox.readonly',
              (value) => !!value,
            ),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode !== 'text';
            },
          },
        ),
        epc.subsections.subsection(
          'style.checkbox',
          'Box',
          [
            epc.fields.colorField('style.checkbox.fill', 'Background', { allowNone: false }),
            ...epc.utils.conditionalFields(
              [
                epc.fields.colorField('style.checkbox.border', 'Outline', {
                  allowGradient: false,
                }),
              ],
              'content.checkbox.readonly',
              (value) => !!value,
            ),
            epc.fields.radiusField('style.checkbox.tileRadius'),
            epc.utils.conditionalField(
              epc.fields.sliderField('style.checkbox.checkmarkSize', 'Checkmark size', [
                { min: 8, max: 48, unit: 'px' },
              ]),
              'content.mode',
              (mode, model) =>
                !['box', 'card'].includes(mode) || !getValueByPath(model, 'content.showCheckIcon'),
            ),
            epc.fields.sliderField('style.checkbox.padding', 'Padding', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.checkbox.gap', 'Space between', [
              { min: 0, max: 72, unit: 'px' },
            ]),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode === 'text';
            },
          },
        ),

        epc.subsections.subsection('style.checkbox', 'Options', [
          epc.fields.colorField('style.checkbox.label', 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields('style.fontFamily', 'style.fontWeight'),
          epc.fields.fontStyleField('style.fontStyle', 'Style', ['bold']),
          epc.fields.sliderField(`style.fontSize`, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
          epc.utils.conditionalField(
            epc.fields.horizontalAlignField('style.card.heading.align', { label: 'Alignment' }),
            'content.mode',
            (mode) => mode !== 'card',
          ),
        ]),

        epc.subsections.subsection(
          'style.box.subText',
          'Sub text',
          [
            epc.fields.selectFieldFromFormFonts('style.box.subText.fontFamily', 'Font'),
            epc.fields.colorField('style.box.subText.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.utils.observedField(
              epc.fields.sliderField('style.box.subText.fontSize', 'Size', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: CheckboxPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateLineHeightWithFontSize(props, value, 'style.box.subText.lineHeight');
              },
            ),
            epc.utils.observedField(
              epc.fields.sliderField('style.box.subText.lineHeight', 'Line height', [
                { min: 8, max: 72, unit: 'px' },
              ]),
              (props: CheckboxPropsModel, value: ValueWithUnitOfMeasure) => {
                return validateFontSizeWithLineHeight(props, value, 'style.box.subText.fontSize');
              },
            ),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              if (mode !== 'box') {
                return true;
              }
              if (getActiveBinding(model.data, 'optionsList')) {
                return !hasCardBindingField(model.data, 'subheading');
              }
              const subheadings =
                (getValueByPath(model, 'content.checkbox.subheading') as
                  | (string | undefined)[]
                  | undefined) ?? [];
              return !subheadings.some((subheading) => !!subheading);
            },
          },
        ),

        epc.subsections.subsection(
          'style.card.subheading',
          'Card Subheading',
          [
            epc.fields.colorField('style.card.subheading.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.selectFieldFromFontWeights(
              'style.card.subheading.fontWeight',
              'Font Weight',
              'style.fontFamily',
            ),
            epc.fields.fontStyleField('style.card.subheading.fontStyle', 'Style', ['bold']),
            epc.fields.sliderField(`style.card.subheading.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.card.subheading.spaceAbove', 'Space above', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.horizontalAlignField('style.card.subheading.align', {
              label: 'Alignment',
            }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode !== 'card' || !hasCardBindingField(model.data, 'subheading');
            },
          },
        ),

        epc.subsections.subsection(
          'style.card.description',
          'Card Description',
          [
            epc.fields.colorField('style.card.description.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.selectFieldFromFontWeights(
              'style.card.description.fontWeight',
              'Font Weight',
              'style.fontFamily',
            ),
            epc.fields.sliderField(`style.card.description.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.card.description.spaceAbove', 'Space above', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.horizontalAlignField('style.card.description.align', {
              label: 'Alignment',
            }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              return mode !== 'card' || !hasCardBindingField(model.data, 'description');
            },
          },
        ),

        epc.subsections.subsection(
          'style.card.customField',
          'Card Custom Fields',
          [
            epc.fields.colorField('style.card.customField.color', 'Color', {
              allowGradient: false,
              allowNone: false,
            }),
            epc.fields.sliderField(`style.card.customField.fontSize`, 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            epc.fields.sliderField('style.card.customField.spaceAbove', 'Space above', [
              { min: 0, max: 72, unit: 'px' },
            ]),
            epc.fields.horizontalAlignField('style.card.customField.align', {
              label: 'Alignment',
            }),
          ],
          {
            hideWhen: (_id, _resolver, model) => {
              const mode = getValueByPath(model, 'content.mode');
              const customFields = getValueByPath(model, 'content.customFields') as
                | unknown[]
                | undefined;
              return mode !== 'card' || !customFields?.length;
            },
          },
        ),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}

function getInitial(): PartialPropsOf<'checkbox'> {
  return {
    content: {
      title: {
        width: '35%',
      },
      checkbox: {
        values: ['Option 1', 'Option 2'],
      },
      mode: 'text',
      image: {
        width: '100px',
        height: '100px',
        ratio: 'none',
        behavior: 'cover',
      },
    },
  };
}
